import { AuthListQueryParam, ISaveUser, IUser, ListModelItems, queryKeys } from '../../shared';
import { IResponseMessages } from '../../shared/models/response-messages.model';
import { userClient } from '../clients/user.client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';

const client = userClient;
const listKey = queryKeys.USERS;
const itemKey = queryKeys.USER;

export function useUserList(params: AuthListQueryParam, messages?: IResponseMessages) {
    return useQuery<ListModelItems<IUser>, AxiosError>({
        queryKey: [listKey, params],
        queryFn: () => client.getList(params, messages),
    });
}

export function useUser(id: string, messages?: IResponseMessages) {
    return useQuery<IUser, AxiosError>({
        queryKey: [itemKey, id],
        queryFn: () => client.getOne(id, messages),
        enabled: !!id,
    });
}

export function useSaveUser() {
    const queryClient = useQueryClient();
    return useMutation<IUser, AxiosError, { data: ISaveUser; messages: IResponseMessages }>({
        mutationFn: ({ data, messages }) =>
            data?.id
                ? client.updateOne(data.id, { ...data, attributes: undefined }, messages)
                : client.createOne(data, messages),
        onSuccess: async ({ id }) => {
            await queryClient.invalidateQueries({ queryKey: [listKey] });
            await queryClient.invalidateQueries({ queryKey: [itemKey, id] });
        },
    });
}

export function useDeleteUser() {
    const queryClient = useQueryClient();
    return useMutation<void, AxiosError, { id: string; messages: IResponseMessages }>({
        mutationFn: ({ id, messages }) => client.deleteOne(id, messages),
        onSuccess: async () => {
            await queryClient.invalidateQueries({ queryKey: [listKey] });
            await queryClient.invalidateQueries({ queryKey: [itemKey] });
        },
    });
}
